import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client/react';
import cn from 'clsx';
import { COMPONENT_NAME, ICreateData, Shared } from '../../shared';
import AxisCard from './axis-card';
import { Context } from './shared/context';
import {
    IFieldListItem,
    IFormData,
    IFormDataAxis,
    IFormDataAxisDateSeries,
    IFormDataAxisNumericSeries,
    IFormDataAxisSeries,
    IListItem,
} from './shared/interfaces';
import Card from '@components-clean/core/card';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import { CheckboxController } from '@components-clean/core/core-checkbox';
import EditCard from '@components-clean/core/edit-card';
import Loading from '@components-clean/core/loading/loading';
import { TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { FieldDefinitionFieldTypeDto } from '@interfaces-clean/field-rdl-definition.interface';
import { usePanelDetailsContext } from '@pages-clean/admin/study/manage-panels/context';
import PanelReasonForChangeDialog from '@pages-clean/admin/study/manage-panels/tab-content/panel-details/types/panel-reason-for-change-dialog';
import {
    AxisInput,
    PanelInput,
    PanelType,
    TimelineAxisType,
    TimelineDateSeriesType,
    TimelineFieldLogicalOperator,
    TimelineNumericSeriesType,
    TimelineRuleLogicalOperator,
} from 'types/graphql-global-types';
import {
    javascriptDateWithoutTimeFromUTC,
    stringDateWithoutTimeFromLocal,
    stringDateWithTimeFromUtcNonNullable,
} from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { GET_PANEL, SAVE_PANEL } from '@queries/central-monitoring';
import {
    panel_OpraQueries_panel_Panel,
    panel as IPanel,
    panelVariables as IPanelVariables,
    upsertPanel,
    upsertPanelVariables,
} from '@queries/generated/central-monitoring.generated';
import {
    PanelListFields_Panel_timeline_Timeline_leftAxis_Axis as LeftAxis,
    PanelListFields_Panel_timeline_Timeline_rightAxis_Axis as RightAxis,
    AxisFields_Axis_dateSeries_DateSeries as DateSeries,
    AxisFields_Axis_numericSeries_NumericSeries as NumericSeries,
} from '@queries/generated/fragments.generated';
import { getSmPanels } from '@queries/generated/subject-monitoring.generated';
import { GET_SM_PANELS } from '@queries/subject-monitoring';

interface ITimelineProps {
    panel?: panel_OpraQueries_panel_Panel;
    onCreate: (panelRef: string) => Promise<void>;
    onUpdate: () => void;
    onCancelOverride?: () => void;
    canEdit?: boolean;
}

const Timeline = ({ panel, onCancelOverride, onUpdate, onCreate, canEdit }: ITimelineProps) => {
    const { t } = useTranslation('admin', { keyPrefix: 'managePanels.panelDetails.timeline' });
    const { t: tCommon } = useTranslation('common');

    const { hasFilters } = useContext(Context);
    const { organisationId, studyId, listItems } = useContext(Context);
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const { source } = usePanelDetailsContext();

    const [savePanel, { loading: isSaving }] = useMutation<upsertPanel, upsertPanelVariables>(SAVE_PANEL);

    const { data: panelsData } = useQuery<getSmPanels>(GET_SM_PANELS, {
        variables: { organisationId, studyId },
    });

    const studyPanels = useMemo(() => {
        return panelsData?.smPanels?.flatMap((studyPanel) => {
            if (!!studyPanel?.plotTitle && studyPanel.id !== panel?.id) return [studyPanel.plotTitle.trim()];
            return [];
        });
    }, [panelsData, panel?.id]);

    const [isEditing, setIsEditing] = useState(!panel);

    const [showReasonForChangeDialog, setShowReasonForChangeDialog] = useState(false);
    const [showResetGridDialog, setShowResetGridDialog] = useState(false);

    const mapAxisForLoading = (axis?: LeftAxis | RightAxis | null) => {
        if (!axis) return undefined;

        const getRuleValue = (field: IFieldListItem | undefined, value: string | IListItem | null) => {
            if (field == null || value == null) {
                return null;
            }

            switch (field.fieldType) {
                case FieldDefinitionFieldTypeDto.BOOL:
                    return listItems.booleanOperators.find((o) => o.id === value.toString());
                case FieldDefinitionFieldTypeDto.DATE:
                case FieldDefinitionFieldTypeDto.PARTIAL_DATE:
                    return javascriptDateWithoutTimeFromUTC(value as string);
                default:
                    return value;
            }
        };

        const mapBaseSeries = (series: DateSeries | NumericSeries) => ({
            id: series.id,
            label: series.label,
            rowData: {
                source: listItems.rowData.find((d) => d.id === series.rowData.source),
                rule: series.rowData.rule
                    ? {
                          field1: listItems.fields.find((d) => d.id === series.rowData.rule!.field1),
                          operator1: listItems.operators.find((o) => o.id === series.rowData.rule!.operator1) ?? null,
                          value1: getRuleValue(
                              listItems.fields.find((d) => d.id === series.rowData.rule!.field1),
                              series.rowData.rule.value1,
                          ),
                          logicalOperator:
                              listItems.ruleOperators.find((ro) => ro.id === series.rowData.rule!.logicalOperator) ??
                              null,
                          field2: series.rowData.rule.field2
                              ? listItems.fields.find((d) => d.id === series.rowData.rule!.field2)
                              : null,
                          operator2: listItems.operators.find((o) => o.id === series.rowData.rule!.operator2) ?? null,
                          value2: getRuleValue(
                              listItems.fields.find((d) => d.id === series.rowData.rule!.field2),
                              series.rowData.rule.value2,
                          ),
                      }
                    : undefined,
            },
        });

        const series =
            axis.type === TimelineAxisType.DATE
                ? (axis.dateSeries ?? []).map((dateSeries) => ({
                      ...mapBaseSeries(dateSeries),
                      type:
                          listItems.dateSeriesTypes.find((dt) => dt.id === dateSeries.type) ??
                          listItems.dateSeriesTypes[0],
                      startField: listItems.fields.find((f) => f.id === dateSeries.startField),
                      endField: listItems.fields.find((f) => f.id === dateSeries.endField),
                      plotLabel: listItems.fields.find((f) => f.id === dateSeries.plotLabel),
                  }))
                : (axis.numericSeries ?? []).map((numericSeries) => ({
                      ...mapBaseSeries(numericSeries),
                      type:
                          listItems.numericSeriesTypes.find((nt) => nt.id === numericSeries.type) ??
                          listItems.numericSeriesTypes[0],
                      yPlotField: listItems.fields.find((f) => f.id === numericSeries.yPlotField),
                      dateField: listItems.fields.find((f) => f.id === numericSeries.dateField),
                      threshold: numericSeries.threshold && {
                          value: numericSeries.threshold.value && {
                              upper: numericSeries.threshold.value.upper,
                              lower: numericSeries.threshold.value.lower,
                          },
                          field: numericSeries.threshold.field && {
                              upper: listItems.fields.find((f) => f.id === numericSeries.threshold!.field?.upper),
                              lower: listItems.fields.find((f) => f.id === numericSeries.threshold!.field?.lower),
                          },
                      },
                  }));

        return {
            type: listItems.axisTypes.find((item) => item.id === axis.type) ?? listItems.axisTypes[0],
            label: axis.title,
            unit: axis.unit,
            series,
        };
    };

    const defaultFormValues = useMemo(
        () => ({
            isActive: panel?.active ?? false,
            configId: panel?.configId ?? '',
            plotTitle: panel?.name ?? '',

            leftAxis: mapAxisForLoading(panel?.timeline?.leftAxis) ?? {
                type: listItems.axisTypes.find((item) => item.id === TimelineAxisType.DATE),
                label: '',
                series: [],
            },
            rightAxis: mapAxisForLoading(panel?.timeline?.rightAxis) ?? {
                type: listItems.axisTypes.find((item) => item.id === TimelineAxisType.NONE),
                label: '',
                series: [],
            },
        }),
        [panel, listItems.axisTypes],
    );

    const {
        control,
        reset,
        setValue,
        handleSubmit,
        watch,
        formState: { isDirty, isSubmitting, isValid },
    } = useForm<IFormData>({
        defaultValues: defaultFormValues,
        mode: 'all',
    });

    const leftAxis = watch('leftAxis');
    const rightAxis = watch('rightAxis');

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [isDirty]);

    const submit = async (reasonForChange?: string) => {
        await handleSubmit(async (formData) => {
            try {
                const mapAxisForSaving = (axis: IFormDataAxis) => {
                    const getRuleValue = (field?: IFieldListItem | null, value?: string | IListItem | Date | null) => {
                        if (!field || !value) return null;
                        switch (field?.fieldType) {
                            case FieldDefinitionFieldTypeDto.BOOL:
                                return (value as IListItem).id === 'true';
                            case FieldDefinitionFieldTypeDto.DATE:
                            case FieldDefinitionFieldTypeDto.PARTIAL_DATE:
                                return stringDateWithoutTimeFromLocal(value as Date);
                            default:
                                return value;
                        }
                    };

                    const mapBaseSeries = (series: IFormDataAxisSeries) => ({
                        id: series.id,
                        label: series.label ?? '',
                        rowData: {
                            source: series.rowData.source!.id,
                            rule: series.rowData.rule
                                ? {
                                      field1: series.rowData.rule.field1?.id ?? '',
                                      operator1: series.rowData.rule.operator1?.id as TimelineFieldLogicalOperator,
                                      value1: getRuleValue(series.rowData.rule.field1, series.rowData.rule.value1),
                                      logicalOperator: series.rowData.rule.logicalOperator
                                          ? (series.rowData.rule.logicalOperator?.id as TimelineRuleLogicalOperator)
                                          : null,
                                      field2: series.rowData.rule?.field2?.id ?? null,
                                      operator2:
                                          (series.rowData.rule?.operator2?.id as TimelineFieldLogicalOperator) ?? null,
                                      value2: getRuleValue(series.rowData.rule.field2, series.rowData.rule.value2),
                                  }
                                : undefined,
                        },
                    });

                    const axisInput: AxisInput = {
                        type: axis.type.id as TimelineAxisType,
                        title: axis.label,
                        unit: axis.unit,
                    };

                    if (axis.type.id === TimelineAxisType.DATE) {
                        axisInput.dateSeries = axis.series.map((series) => {
                            const dateSeries = series as IFormDataAxisDateSeries;
                            return {
                                ...mapBaseSeries(dateSeries),
                                type: dateSeries.type.id as
                                    | TimelineDateSeriesType.SINGLE_DATE
                                    | TimelineDateSeriesType.DATE_RANGE,
                                startField: dateSeries.startField!.id,
                                endField: dateSeries.endField?.id ?? null,
                                plotLabel: dateSeries.plotLabel!.id,
                            };
                        });
                    } else {
                        axisInput.numericSeries = axis.series.map((series) => {
                            const numericSeries = series as IFormDataAxisNumericSeries;
                            return {
                                ...mapBaseSeries(numericSeries),
                                type: numericSeries.type.id as
                                    | TimelineNumericSeriesType.LINE
                                    | TimelineNumericSeriesType.SCATTER,
                                yPlotField: numericSeries.yPlotField!.id,
                                dateField: numericSeries.dateField!.id,
                                threshold: numericSeries.threshold && {
                                    value: numericSeries.threshold.value && {
                                        upper: numericSeries.threshold.value.upper ?? null,
                                        lower: numericSeries.threshold.value.lower ?? null,
                                    },
                                    field: numericSeries.threshold.field && {
                                        upper: numericSeries.threshold.field.upper?.id ?? null,
                                        lower: numericSeries.threshold.field.lower?.id ?? null,
                                    },
                                },
                            };
                        });
                    }

                    return axisInput;
                };

                const savingPanel: PanelInput = {
                    id: panel?.id,
                    configId: panel?.configId,

                    organisationId,
                    studyId,

                    panelTheme: panel?.panelTheme,
                    panelType: PanelType.TIMELINE,

                    name: formData.plotTitle,
                    active: formData.isActive,

                    timeline: {
                        leftAxis: mapAxisForSaving(formData.leftAxis),
                        rightAxis:
                            formData.rightAxis.type.id !== TimelineAxisType.NONE
                                ? mapAxisForSaving(formData.rightAxis)
                                : undefined,
                    },
                };

                const result = await savePanel({
                    variables: {
                        panel: savingPanel,
                        reason: reasonForChange ?? '',
                    },
                });
                ShowSuccess(t('notifications.saveSuccess'));
                setIsEditing(false);

                if (panel) {
                    onUpdate();
                } else {
                    await onCreate(result.data?.upsertPanel?.id!);
                }
            } catch (error) {
                const errorMessage = t('notifications.saveError');
                ShowError(errorMessage);
                throw error;
            }
        })();
    };

    const content = isSaving ? (
        <div className={`loading-overlay ${!isSaving ? 'fade-out' : ''}`}>
            <Loading />
        </div>
    ) : (
        <>
            <div className="column column-1">
                <div className="top-row">
                    <CheckboxController
                        data-testid="is-active"
                        label={t('labels.active')}
                        name="isActive"
                        control={control}
                        disabled={!isEditing}
                    />
                    <TextBoxController
                        label={t('labels.configId')}
                        data-testid="config-id"
                        name="configId"
                        control={control}
                        readOnly
                        copyBtn
                    />
                </div>
                <TextBoxController
                    label={t('labels.plotTitle')}
                    data-testid="plot-title"
                    name="plotTitle"
                    control={control}
                    required
                    maxLength={30}
                    readOnly={!isEditing}
                    validateRules={{
                        uniqueTitle: (value) => {
                            const plotTitle = (value as string)?.trim();
                            if (plotTitle && studyPanels?.includes(plotTitle))
                                return t('validationErrors.uniquePlotTitle');
                        },
                    }}
                />

                <dl className="audit-info">
                    <dt>{t('labels.created')}:</dt>
                    <dd>
                        {panel && (
                            <>
                                {stringDateWithTimeFromUtcNonNullable(panel.created)} by {panel.createdBy?.firstName}{' '}
                                {panel.createdBy?.lastName}
                            </>
                        )}
                    </dd>

                    <dt>{t('labels.updated')}:</dt>
                    <dd>
                        {panel && (
                            <>
                                {stringDateWithTimeFromUtcNonNullable(panel.updated)} by {panel.updatedBy?.firstName}{' '}
                                {panel.updatedBy?.lastName}
                            </>
                        )}
                    </dd>
                </dl>
            </div>
            <div className="column column-2">
                <AxisCard side="left" control={control} setValue={setValue} isEditing={isEditing} />
                <AxisCard side="right" control={control} setValue={setValue} isEditing={isEditing} />
            </div>
        </>
    );

    const isSaveDisabled = useMemo(() => {
        if (isSubmitting || !isValid) {
            return true;
        }

        if (!isDirty) {
            return true;
        }

        if (leftAxis.series.length === 0) {
            return true;
        }

        return rightAxis.type.id !== TimelineAxisType.NONE && rightAxis.series.length === 0;
    }, [isSubmitting, isDirty, leftAxis.series, rightAxis.series, rightAxis.series, isValid]);

    // Show a regular card if we want to hide the edit button (on the ALL panels tab)
    const CardToUse = source === 'All' ? Card : EditCard;

    return (
        <>
            {showReasonForChangeDialog && (
                <PanelReasonForChangeDialog
                    onCancel={() => setShowReasonForChangeDialog(false)}
                    onSubmit={async (reason) => {
                        setShowReasonForChangeDialog(false);
                        await submit(reason);
                    }}
                />
            )}
            <CardToUse
                title={panel ? `${t('titles.edit')} ${panel.recordId}` : t('titles.add')}
                className={cn(Shared.className, 'timeline', 'k-h-full')}
                data-testid={Shared.dataTestId}
                isEditMode={isEditing}
                onEdit={() => setIsEditing(true)}
                onSave={async () => {
                    if (panel && isValid) {
                        setShowReasonForChangeDialog(true);
                    } else {
                        if (hasFilters) {
                            setShowResetGridDialog(true);
                            return;
                        }
                        await submit();
                    }
                }}
                onCancel={async () => {
                    if (componentHasChanges(COMPONENT_NAME)) {
                        const confirmed = await confirmDiscardChanges();
                        if (!confirmed) {
                            return;
                        }
                        setComponentHasChanges(COMPONENT_NAME, false);
                    }
                    setIsEditing(false);
                    reset(defaultFormValues);
                    onCancelOverride?.();
                }}
                saveDisabled={isSaveDisabled}
                disabled={!canEdit}
            >
                {showResetGridDialog && (
                    <ConfirmationDialog
                        dataTestId="filter-clear-warning-dialog"
                        title={tCommon('grid.filterSortWarning.title')}
                        onSubmit={async () => {
                            setShowResetGridDialog(false);
                            await submit();
                        }}
                        onClose={() => {
                            setShowResetGridDialog(false);
                        }}
                        primaryBtnLabel={tCommon('common:continue')}
                    >
                        {tCommon('grid.filterSortWarning.body')}
                    </ConfirmationDialog>
                )}
                {content}
            </CardToUse>
        </>
    );
};

interface IProps {
    panelRef?: string;
    createData?: ICreateData;
    canEdit?: boolean;
}

const Wrapper = ({ panelRef, createData, canEdit }: IProps) => {
    const {
        loading: isLoading,
        refetch,
        data,
    } = useQuery<IPanel, IPanelVariables>(GET_PANEL, {
        variables: { panelId: panelRef },
        skip: !panelRef,
        fetchPolicy: 'no-cache',
    });

    if (isLoading) {
        return <Loading />;
    }

    return (
        <Timeline
            key={data?.panel?.auditId} // This is needed to ensure the panel properly resets.
            panel={data?.panel ?? undefined}
            onCancelOverride={createData?.onCancel}
            onCreate={async (newPanelRef: string) => {
                await createData?.onCreate(newPanelRef);
            }}
            onUpdate={async () => {
                await refetch();
            }}
            canEdit={canEdit}
        />
    );
};

export default Wrapper;
